/*
 * StatusPill: a coloured dot and label for a state. Knows every subscription (license) state, subscription,
 * invoice and payment state, translated from `common:states.*`:
 *   <StatusPill kind="license" value={server.licenseState} />
 *   <StatusPill kind="payment" value="processing" />     (pulses while in progress)
 *   <StatusPill tone="success">{custom}</StatusPill>      free-form
 */
import type { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import type { InvoiceState, LicenseState, PaymentStatus, SubscriptionStatus } from '@/api/types'
import { cn } from '@/lib/cn'
import { TONE_CLASSES, type Tone } from './Badge'

const LICENSE: Record<LicenseState, Tone> = {
  pending_activation: 'brand',
  trial: 'indigo',
  valid: 'success',
  past_due: 'warning',
  grace: 'warning',
  degraded: 'danger',
  expired: 'danger',
  locked: 'danger',
}
const SUBSCRIPTION: Record<SubscriptionStatus, Tone> = {
  incomplete: 'brand',
  trialing: 'indigo',
  active: 'success',
  past_due: 'warning',
  pending: 'brand',
  ended: 'danger',
}
const INVOICE: Record<InvoiceState, Tone> = {
  open: 'brand',
  paid: 'success',
  failed: 'danger',
  void: 'neutral',
  refunded: 'neutral',
}
const PAYMENT: Record<PaymentStatus, Tone> = {
  pending: 'neutral',
  processing: 'brand',
  paid: 'success',
  failed: 'danger',
  refunded: 'neutral',
}
const PULSE = new Set(['pending_activation', 'processing'])

type Known =
  | { kind: 'license'; value: LicenseState }
  | { kind: 'subscription'; value: SubscriptionStatus }
  | { kind: 'invoice'; value: InvoiceState }
  | { kind: 'payment'; value: PaymentStatus }

type Free = { kind?: undefined; tone: Tone; children: ReactNode; pulse?: boolean }

export function StatusPill(props: (Known | Free) & { className?: string; size?: 'sm' | 'md' }) {
  const { t } = useTranslation('common')
  let tone: Tone
  let label: ReactNode
  let pulse: boolean
  if (props.kind) {
    const maps = { license: LICENSE, subscription: SUBSCRIPTION, invoice: INVOICE, payment: PAYMENT }
    tone = (maps[props.kind] as Record<string, Tone>)[props.value] ?? 'neutral'
    label = t(`states.${props.kind}.${props.value}` as 'states.license.valid')
    pulse = PULSE.has(props.value)
  } else {
    tone = props.tone
    label = props.children
    pulse = Boolean(props.pulse)
  }
  const c = TONE_CLASSES[tone]
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1.5 rounded-full font-semibold whitespace-nowrap',
        props.size === 'sm' ? 'h-5 px-2 text-[11px]' : 'h-6 px-2.5 text-12',
        c.pill,
        props.className,
      )}
    >
      <span className="relative flex size-1.5" aria-hidden="true">
        {pulse ? (
          <span className={cn('absolute inset-0 animate-ping rounded-full opacity-60', c.dot)} />
        ) : null}
        <span className={cn('relative size-1.5 rounded-full', c.dot)} />
      </span>
      {label}
    </span>
  )
}
